import { BrowserRouter as Router, Route, Routes } from "react-router-dom";

import React, { useEffect, useState, useRef, lazy, Suspense } from 'react';
import './App.css';
import Home from './Components/Home';
import Footer from './Components/Footer';
import FacebookIcon from './Components/FacebookIcon';
import InstagramIcon from './Components/InstagramIcon';
import TripadvisorIcon from './Components/TripadvisorIcon';
import SiteHeader from './Components/SiteHeader';
import AnalyticsTracker from './Components/AnalyticsTracker';
import { notifications } from '@mantine/notifications';
import { useTours } from './hooks/useTours';
import { useBundles } from './hooks/useBundles';
import { trackEvent } from './utils/analytics';

import {
    IconHome,
    IconInfoCircle,
    IconTicket,
    IconMenu2,
    IconShip,
    IconMail,
    IconPhoneCall,
} from '@tabler/icons-react';
import type { Bundle, FollowUsItem, SiteUrl, Tour } from './types';

const About = lazy(() => import('./Components/About'));
const BookNow = lazy(() => import('./Components/BookNow'));
const AllTours = lazy(() => import('./Components/AllTours'));
const Contact = lazy(() => import('./Components/Contact'));
const CruiseTours = lazy(() => import('./Components/CruiseTours'));
const TourBody = lazy(() => import('./Components/TourBody'));
const ThankYou = lazy(() => import('./Components/ThankYou'));
const ThankYouContact = lazy(() => import('./Components/ThankYouContact'));
const FAQ = lazy(() => import('./Components/FAQ'));
const Safety = lazy(() => import('./Components/Safety'));
const Covid = lazy(() => import('./Components/Covid'));
const AdminRoutes = lazy(() => import('./admin'));

//Begin importing grid images
//@TODO: This is currently hard-coded, there may be a more efficient way
//@TOOD: These images are a mixture of png and jpg, we may want to convert to an optimal file format
import nimLiImage from './images/nimLiSquare.webp';
import waterfallImage from './images/waterfalls.webp';
import zipliningImage from './images/ziplining.webp';
import spiceFarmImage from './images/spiceFarm.webp';
import lubaantunImage from './images/lubaantun.webp';
import riverTubingImage from './images/riverTubing.webp';
import inlandBlueHoleImage from './images/inlandBlueHole.webp';
import blueHoleImage from './images/blueHole.webp';
import atmImage from './images/atm.webp';
import caveTubingImage from './images/caveTubing.webp';
import twinCitiesImage from './images/twinCities.webp';
import zooImage from './images/zoo.webp';
import yachtImage from './images/yacht.webp';


// Helper function to process Vite's import.meta.glob results
type GlobResult = Record<string, { default?: string } | string>;
type ImportAllResult = Record<string, string> & { numKeys: number };

function importAll(globResult: GlobResult): ImportAllResult {
    const ret = { numKeys: 0 } as ImportAllResult;
    let i = 0;
    for (const path in globResult) {
        // Extract just the filename from the full path
        const key = path.split('/').pop() || path;
        const entry = globResult[path];
        const resolved = typeof entry === 'string' ? entry : (entry.default || '');
        ret[key] = resolved;
        i++;
    }
    ret["numKeys"] = i;
    return ret;
}

// Pre-import all tour images using Vite's import.meta.glob
const nimLiGridImages = import.meta.glob('./images/nimLi/grid/*.webp', { eager: true });
const nimLiSlideImages = import.meta.glob('./images/nimLi/*.webp', { eager: true });
const waterfallsGridImages = import.meta.glob('./images/waterfalls/grid/*.webp', { eager: true });
const waterfallsSlideImages = import.meta.glob('./images/waterfalls/*.webp', { eager: true });
const zipliningGridImages = import.meta.glob('./images/ziplining/grid/*.webp', { eager: true });
const zipliningSlideImages = import.meta.glob('./images/ziplining/*.webp', { eager: true });
const spiceFarmGridImages = import.meta.glob('./images/spiceFarm/grid/*.webp', { eager: true });
const spiceFarmSlideImages = import.meta.glob('./images/spiceFarm/*.webp', { eager: true });
const lubaantunGridImages = import.meta.glob('./images/lubaantun/grid/*.webp', { eager: true });
const lubaantunSlideImages = import.meta.glob('./images/lubaantun/*.webp', { eager: true });
const riverTubingGridImages = import.meta.glob('./images/riverTubing/grid/*.webp', { eager: true });
const riverTubingSlideImages = import.meta.glob('./images/riverTubing/*.webp', { eager: true });
const inlandBlueHoleGridImages = import.meta.glob('./images/inlandBlueHole/grid/*.webp', { eager: true });
const inlandBlueHoleSlideImages = import.meta.glob('./images/inlandBlueHole/*.webp', { eager: true });
const blueHoleGridImages = import.meta.glob('./images/blueHole/grid/*.webp', { eager: true });
const blueHoleSlideImages = import.meta.glob('./images/blueHole/*.webp', { eager: true });
const atmGridImages = import.meta.glob('./images/atm/grid/*.webp', { eager: true });
const atmSlideImages = import.meta.glob('./images/atm/*.webp', { eager: true });
const caveTubingGridImages = import.meta.glob('./images/caveTubing/grid/*.webp', { eager: true });
const caveTubingSlideImages = import.meta.glob('./images/caveTubing/*.webp', { eager: true });
const twinCitiesGridImages = import.meta.glob('./images/twinCities/grid/*.webp', { eager: true });
const twinCitiesSlideImages = import.meta.glob('./images/twinCities/*.webp', { eager: true });
const zooGridImages = import.meta.glob('./images/zoo/grid/*.webp', { eager: true });
const zooSlideImages = import.meta.glob('./images/zoo/*.webp', { eager: true });
const yachtGridImages = import.meta.glob('./images/yacht/grid/*.webp', { eager: true });
const yachtSlideImages = import.meta.glob('./images/yacht/*.webp', { eager: true });
const homeSlideImagesGlob = import.meta.glob('./images/home/*.webp', { eager: true });

const siteUrls: SiteUrl[] = [
    { logo: <IconHome size={18} />, url: "/", text: "Home" },
    { logo: <IconInfoCircle size={18} />, url: "/About", text: "About"},
    { logo: <IconTicket size={18} />, url: "/BookNow", text: "Book Now"},
    { logo: <IconMenu2 size={18} />, url: "/tours/AllTours", text: "All Tours", id: "desktopLand" },
    { logo: <IconShip size={18} />, url: "/tours/CruiseTours", text: "Cruise Tours", id: "desktopCruise"},
    { logo: <IconMail size={18} />, url: "/Contact", text: "Contact Us"},
    { logo: <IconPhoneCall size={18} />, url: "https://wa.me/5016374477", text: "WhatsApp: +5016374477", },
    { logo: <IconMail size={18} />, url: "mailto:placencia.action.tours@outlook.com", text: "placencia.action.tours@outlook.com",}


];

const followUs: FollowUsItem[] = [
    { alt: "Facebook", imgComponent: FacebookIcon, url: "https://www.facebook.com/pages/category/Tour-Agency/Placencia-Action-Tours-537314880112426/" },
    { alt: "Instagram", imgComponent: InstagramIcon, url: "https://www.instagram.com/placencia_action_tours/" },
    { alt: "Tripadvisor", imgComponent: TripadvisorIcon, url: "https://www.tripadvisor.com/Attraction_Review-g291977-d20387654-Reviews-Placencia_Action_Tours-Placencia_Stann_Creek.html" }
]; 

//Variables to shorten the content for nimLiPunit
const nimLiContent = ["Nestled between the lush broadleaf rainforest and the the foothills of the Maya mountains, resting between clear mountain streams lies Nim Li Punit. This site is very well-preserved as the relatively high population flourished at its peak around 500 AD.",
                      "Nim Li Punit is well-known for the large amount of stelae (stone tablets) commemorating the ancient royalty, culture, and general records of the site. You will also see an astonishingly well-preserved Mayan basketball court near the plaza of this ancient city."];
const nimLiIncludes = ["An authentic Belizean meal",
                       "Comfortable, air-conditioned transport",
                       "Water"]; 
const nimLiWhatToBring = ["A good pair of hiking/athletic shoes",
                          "Sunscreen",
                          "Bug spray"];

//Variables to shorten the content for the waterfalls tour
const waterfallsContent = ["Located on the southeast coast of southern Belize are the beautiful Stan Creek Hills the home to beautiful havens like this. Bask under the beautiful southern water falls and enjoy some good family fun. Rum punch is an option!"]; 
const waterfallsWhatToBring = ["Clothes and shoes you are comfortable with getting wet",
                               "A Change of clothes",
                               "Towel",
                               "Sunscreen",
                               "Bug spray"];

//Variables to shorten the content for the ziplining tour
const zipliningContent = ["Set within the Southern foot hills of the beautiful Maya Mountain Range is the awesome ziplining experience at Maya Sky Canopy. With a total of 6 platforms the highest of which being over 400 feet high and 1000 feet across, you are sure to have a blast! Guests can expect to travel at speeds of up to 38 miles per hour through the lush rainforest."]; 
const zipliningIncludes = ["An authentic Belizean meal",
                           "Comfortable, air-conditions transport",
                           "Water",
                           "Ziplining gear and safety helmet"];
const zipliningWhatToBring = ["A good pair of hiking/athletic shoes",
                              "Sunscreen",
                              "Bug spray",
                              "Courage of the Jaguar"];


//Variables to shorten the content for the spice farm tour
const spiceFarmContent = ["Between the foothills of the Maya Mountains and the broadleaf rainforest lies this showcase of Belize's natural botanicals. Here you will get to go on a botanical tour and get a chance to see, touch, smell and taste a variety of tropical plants,fruits and herbs. This is topped off with an authentic Belizean meal at the beautiful spice farm dining room."];

//Variables to shorten the content for the lubaantun tour
const lubaantunContent = [
    "Welcome to Lubaantun, an ancient Maya city located in the southern part of Belize! This tour will take you on a journey through time, exploring the rich history and culture of the Maya people.",
    "Our adventure begins at the entrance of Lubaantun, where you will be greeted by a knowledgeable guide who will take you through the site's most important landmarks. As you make your way through the ruins, you'll learn about the intricate architectural details of the various buildings and structures that once stood tall in this city.",
    "Also known as \"The Place of the Fallen Stones,\" Lubaantun is well known for the huge difference in architectural design as in comparison to the other sites found in the rest of Belize. The pyramids here are built of black slate with no mortar, rather than the typical limestone blocks - it resembles more of an ancient military stronghold than a city.",
    "You'll have the opportunity to see the impressive ball court, where the Maya played their famous ball game, and the ceremonial center where they conducted religious rituals. You'll also visit the residential areas where the common people lived and worked, as well as the palace where the Maya rulers resided.",
    "One of the highlights of the tour is the opportunity to explore the unique and enigmatic Lubaantun crystal skull discovered in these ruins. This mysterious artifact has been the subject of much speculation and intrigue. The skull is rumored in the pseudo-science community to have ancient mayan mystical powers as a bringer of both doom and enlightenment. On this tour you'll have the chance to learn more about its fascinating history.",
    "Throughout the tour, your guide will share stories and legends of the Maya people, their beliefs, and their way of life. You'll gain a deep understanding of the culture and society that flourished in Lubaantun more than a thousand years ago.",
    "This tour is perfect for history buffs, culture enthusiasts, and anyone who wants to experience the magic and mystery of ancient civilizations. Don't miss this opportunity to explore one of the most important and impressive Maya sites in Belize. Book your Lubaantun tour today!",
];

//Variables to shorten the content for jungle river tubing tour
const jungleTubingContent = ["River rafting on inner tubes through the foothills of beautiful southern Belize on a crystal clear riverine system through dense, tropical, broadleaf rainforest followed by an authentic delicious Belizean coconut rice & beans meal."];
const jungleTubingWhatToBring = waterfallsWhatToBring; 

//Variables to shorten the content for the inland blue hole tour 
const inlandBlueHoleContent = ["The Mayan shamans used these series of amazing cave systems for their religious ceremonies as they believed this was the ancient dwelling place of their dark lords. The underworld known as Xibalba translated into hell must be traversed by the soul in order to ascend into heaven.",
                               "In the modern age we explore these caves and learn and appreciate all about the mayan culture. We top it off with a swim in the natural pool created by a sinkhole in the side of the mountain known as the inland blue hole."]; 
const inlandBlueHoleWhatToBring = waterfallsWhatToBring; 

//Variables to shorten the content for the blue hole flyover tour 
const blueHoleContent = ["Measuring 1,000 feet across and approximately 450 feet deep, it is the largest blue hole in the ocean. Passengers are expected to take an approximate one hour flight in which they will get to make multiple passes over the majestic blue hole and gaze at the natural wonder.",
                         "The blue hole was formerly an above ground cavern but when water receded due to geological shifts millions of years ago the roof of the cavern caved in forming today what we know as the blue hole. Through this process the deepest sink hole in the western hemisphere was formed.",
                         "The Great Blue Hole is at the top of bucket lists not only for its natural beauty from above, but for scuba diving exploration. If you are a certified scuba diver with intermediate experience, do not hesitate to contact us and we will arrange it so that you can explore this natural wonder from the inside."];
const blueHoleIncludes = ["Comfortable, air-conditioned transport",
                          "A private flight for your group"];
const blueHoleWhatToBring = ["Camera"]; 

//Variables to shorten the content of the ATM Cave Tour 
const ATMContent = ["Known as the place of the stone sepulchre. This site is an ancient underground Maya burial site. A hiking and adventure experience not for the faint of heart. This is no leisure tour.",
    "Ranked as one of the top ten caves in the world by National Geographic Society you are sure to be amazed setting your feet in the same place that ancient mystical Mayan Shamans once performed sacrifices to their Old Ancestral gods"]; 

//Variables to shorten the content of the Cave Tubing Tour
const caveTubingContent = ["Located in the centre of the country of Belize, this cave system is located within a National Park it is home to hiking trails through the rainforest after which you take a leisurely float on inner tubes through a 1.5 mile long cave system with a river running through them.",
    "Natural limestone formations, archaeological history of the Maya and good old family fun and relaxation are all part of this wonderful cave exploration experience."]; 

//Variables to shorten the content of the Twin Cities Tour
const twinCitiesContent = ["Located in the Western most reaches of Belize are the two mystical and ancient Maya sites of Xunantunich and Cahal Pech. These two structures date back to the Classic period of the Maya and represents the grandeur of the Maya civilization whilst they were at their highest height. Learn about the history of the Maya and the archaeological, astronomical and architectural wonders performed by these Palaeolithic people. Guests will also get to enjoy the beautiful surrounding of the lush rain forests of Belize."];

//Variables to shorten the content of the Belize Zoo Tour 
const belizeZooContent = ["In the mid 1980�s Sharon Matola and a team of American, Canadian and British animal conservationist came to Belize to do a documentary on tropical exotic species of animals.",
    "In their efforts they acquired animals that have no business being kept as pets amongst them: Jaguars, boa-constrictors, tapirs, birds of prey, and a melee of other exotic animals. After the movie production Sharon was left with the difficult task of finding a home for these exotic creatures. It is with this effort she made the Belize Tropical Education center.",
    "To this day Sharon is the only person in Belize allowed to take care of wild animals. In fact the only animals located at the zoo are animals that cannot be rehabilitated and returned to the wild. All 5 of Belize�s exotic cats are found at the Belize Zoo. Also the beautiful Toucan, the Scarlett Macaw and over 40 other Tropical species of snakes, birds, fish and mammals. A perfect place to see what Belize rainforest holds."];

//Variables to shorten the content of Caribbean Yacht Charter Tour 
const yachtContent = ["This 150ft behemoth of a boat has some of the best amenities that some people wish to have in their home. With a full living area a top deck lounge and two air conditioned bedrooms on the bottom deck the passengers riding in this bad boy are sure to feel like royalty, especially as we travel to some the most exclusive private islands and coral reef wildlife reservoirs of Belize.",
                      "Guests can either select from one of our pre-determined destinations or go wild with it and pick wherever they want to go. We invite you to consider this as your way to see beautiful Belize as we host you guys on what is the largest barrier reef this side of the world.",
                      "Email for inquiries and quotes. Have some fun in the sun!"];

// Fallback tours used when database is unavailable or empty
const staticTours: Tour[] = [
    { priceChild: 0, gridImages: importAll(nimLiGridImages), slideImages: importAll(nimLiSlideImages), whatToBring: nimLiWhatToBring, includes: nimLiIncludes, content: nimLiContent, price: 120, name: "nimLi", cardImage: nimLiImage, url: "/tours/NimLiPunit", text: "Nim Li Punit Mayan Ruins", cruise: true, ShortDesc: "Seeing how the ancient Mayans played basketball? The ball is in your court." },
    { priceChild: 0, gridImages: importAll(waterfallsGridImages), slideImages: importAll(waterfallsSlideImages), whatToBring: waterfallsWhatToBring, includes: nimLiIncludes, content: waterfallsContent, price: 100, name: "waterfalls", cardImage: waterfallImage, url: "/tours/Waterfalls", text: "Waterfall Sightseeing", cruise: true, ShortDesc: "Stroll through natural waterfalls in the rainforest with a picnic at the end." },
    { priceChild: 0, gridImages: importAll(zipliningGridImages), slideImages: importAll(zipliningSlideImages), whatToBring: zipliningWhatToBring, includes: zipliningIncludes, content: zipliningContent, price: 130, name: "ziplining", cardImage: zipliningImage, url: "/tours/Ziplining", text: "Rainforest Ziplining", cruise: true, ShortDesc: "Race the howler monkeys through jungle canopies in a breathtaking experience." },
    { priceChild: 0, gridImages: importAll(spiceFarmGridImages), slideImages: importAll(spiceFarmSlideImages), whatToBring: nimLiWhatToBring, includes: nimLiIncludes, content: spiceFarmContent, price: 110, name: "spiceFarm", cardImage: spiceFarmImage, url: "/tours/SpiceFarm", text: "Spice Farm", cruise: true, ShortDesc: "A foodie's paradise where you will try the best of Belizian cuisine" },
    { priceChild: 0, gridImages: importAll(lubaantunGridImages), slideImages: importAll(lubaantunSlideImages), whatToBring: nimLiWhatToBring, includes: nimLiIncludes, content: lubaantunContent, price: 120, name: "lubaantun", cardImage: lubaantunImage, url: "/tours/Lubaantun", text: "Lubaantun Mayan Ruins", cruise: false, ShortDesc: "Explore the Mayan stronghold where the original crystal skull was found." },
    { priceChild: 0, gridImages: importAll(riverTubingGridImages), slideImages: importAll(riverTubingSlideImages), whatToBring: jungleTubingWhatToBring, includes: nimLiIncludes, content: jungleTubingContent, price: 95, name: "riverTubing", cardImage: riverTubingImage, url: "/tours/RiverTubing", text: "Jungle River Tubing", cruise: true, ShortDesc: "Dip your toes in as you float down the original jungle cruise ride." },
    { priceChild: 0, gridImages: importAll(inlandBlueHoleGridImages), slideImages: importAll(inlandBlueHoleSlideImages), whatToBring: inlandBlueHoleWhatToBring, includes: nimLiIncludes, content: inlandBlueHoleContent, price: 120, name: "inlandBlueHole", cardImage: inlandBlueHoleImage, url: "/tours/InlandBlueHole", text: "Inland Blue Hole & Cave", cruise: false, ShortDesc: "Explore the rainforest and top it off with a swim in a natural blue hole" },
    { priceChild: 0, gridImages: importAll(blueHoleGridImages), slideImages: importAll(blueHoleSlideImages), whatToBring: blueHoleWhatToBring, includes: blueHoleIncludes, content: blueHoleContent, name: "blueHole", cardImage: blueHoleImage, url: "/tours/BlueHoleFlyover", text: "Blue Hole Flyover", cruise: false, ShortDesc: "Hop aboard a private plane to fly your party over the great blue hole." },
    { priceChild: 0, gridImages: importAll(atmGridImages), slideImages: importAll(atmSlideImages), whatToBring: nimLiWhatToBring, includes: nimLiIncludes, content: ATMContent, price: 365, name: "atm", cardImage: atmImage, url: "/tours/ATMCave", text: "ATM Cave Tour", cruise: false, ShortDesc: "Visit one of the world's ranked top 10 caves for this intense adventure tour." },
    { priceChild: 0, gridImages: importAll(caveTubingGridImages), slideImages: importAll(caveTubingSlideImages), whatToBring: nimLiWhatToBring, includes: nimLiIncludes, content: caveTubingContent, price: 175, name: "caveTubing", cardImage: caveTubingImage, url: "/tours/CaveTubing", text: "Cave Tubing", cruise: false, ShortDesc: "Take a relaxing tube ride through the rainforest and ancient Mayan ritual caves" },
    { priceChild: 0, gridImages: importAll(twinCitiesGridImages), slideImages: importAll(twinCitiesSlideImages), whatToBring: nimLiWhatToBring, includes: nimLiIncludes, content: twinCitiesContent, price: 175, name: "twinCities", cardImage: twinCitiesImage, url: "/tours/TwinCities", text: "Twin Cities Mayan Ruins", cruise: false, ShortDesc: "Visit the largest Mayan ruins in Belize in this two-for-one excursion" },
    { priceChild: 0, gridImages: importAll(zooGridImages), slideImages: importAll(zooSlideImages), whatToBring: nimLiWhatToBring, includes: nimLiIncludes, content: belizeZooContent, price: 170, name: "zoo", cardImage: zooImage, url: "/tours/BelizeZoo", text: "Belize Zoo", cruise: false, ShortDesc: "See all 5 of exotic cats native to Belize and many other exotic tropical species." },
    { priceChild: 0, gridImages: importAll(yachtGridImages), slideImages: importAll(yachtSlideImages), whatToBring: [], includes: [], content: yachtContent, name: "yacht", cardImage: yachtImage, url: "/tours/Yacht", text: "Charter a Yacht", cruise: false, ShortDesc: "Take the keys for our yacht and charter your own voyage through the Caribbean" }
];

staticTours.map(tour => {
    tour.slideImages["name"] = tour.name;
});

const bundles: Bundle[] = [
    /*{
        name: "Cave Tubing and Altun Ha Mayan Ruins Bundle",
        tours: ["caveTubing", "altunHa"], 
        price: 90, 
        priceChild: 70,
        cruise: true, 
        cayes: true 
    }*/
]; 

function computeSavings(tourList: string[], bundlePrice: number, toursArray: Tour[]){
    let sumTourCosts = 0;
    for(let i=0;i<tourList.length;++i){
        const found = toursArray.find(o => o.name===tourList[i]);
        if (found && typeof found.price === 'number') {
            sumTourCosts += found.price;
        }
    }
    return sumTourCosts-bundlePrice;
}
//Compute the savings for each bundle
bundles.map(bundle =>(
    bundle.savings = computeSavings(bundle.tours, bundle.price, staticTours)
));

function App() {
    const [arrivalMode, setArrivalMode] = useState<'hotel' | 'cruise'>('hotel');
    const [arrivalTouched, setArrivalTouched] = useState(false);
    const skipArrivalToastRef = useRef(true);

    // Load tours from database, fall back to static tours
    const { tours: dbTours } = useTours();
    const tours = dbTours.length > 0 ? dbTours : staticTours;

    // Load bundles from database
    const { bundles: dbBundles } = useBundles();

    // Convert database bundles to the Bundle type used by components
    const activeBundles: Bundle[] = dbBundles.map(bundle => ({
        id: bundle.id,
        name: bundle.name,
        description: bundle.description,
        tours: bundle.tourSlugs,
        price: bundle.price,
        priceChild: bundle.priceChild,
        cruise: bundle.cruise,
        cayes: bundle.cayes,
        displayOrder: bundle.displayOrder,
        savings: computeSavings(bundle.tourSlugs, bundle.price, tours),
    }));

    // Recompute static bundle savings when tours change (fallback)
    useEffect(() => {
        bundles.forEach(bundle => {
            bundle.savings = computeSavings(bundle.tours, bundle.price, tours);
        });
    }, [tours]);

    // Use database bundles if available, otherwise use static
    const allBundles = activeBundles.length > 0 ? activeBundles : bundles;

    useEffect(() => {
        if (import.meta.env.MODE !== 'production') return;
        const loadGtag = () => {
            if (document.getElementById('ga-gtag')) return;
            const script = document.createElement('script');
            script.id = 'ga-gtag';
            script.async = true;
            script.src = 'https://www.googletagmanager.com/gtag/js?id=G-SZSP3SQY0M';
            document.head.appendChild(script);
            window.dataLayer = window.dataLayer || [];
            function gtag(...args: unknown[]) { window.dataLayer.push(args); }
            gtag('js', new Date());
            gtag('config', 'G-SZSP3SQY0M');
        };
        if ('requestIdleCallback' in window) {
            window.requestIdleCallback(loadGtag, { timeout: 3000 });
        } else {
            setTimeout(loadGtag, 1500);
        }
    }, []);

    useEffect(() => {
        if (skipArrivalToastRef.current) {
            skipArrivalToastRef.current = false;
            return;
        }
        if (!arrivalTouched) return;
        notifications.show({
            title: 'Embarkation updated',
            message: arrivalMode === 'hotel'
                ? 'Showing tours that fit hotel stays in Placencia.'
                : 'Showing tours that fit cruise schedules from the docks.',
            color: 'brand',
            autoClose: 1500,
        });
    }, [arrivalMode, arrivalTouched]);

    const handleArrivalChange = (mode: 'hotel' | 'cruise') => {
        if (mode === arrivalMode) return;
        setArrivalTouched(true);
        setArrivalMode(mode);
        trackEvent('arrival_mode_change', {
            arrivalMode: mode,
            metadata: {
                from: arrivalMode,
            },
        });
    };

    return (
        <Router
            future={{
                v7_startTransition: true,
                v7_relativeSplatPath: true,
            }}
        >
            <SiteHeader
                tours={tours}
                arrivalMode={arrivalMode}
                onArrivalChange={handleArrivalChange}
            />
            <AnalyticsTracker arrivalMode={arrivalMode} />
            <Routes>
                        <Route
                            path="/"
                            element={
                                <Home
                                    tours={tours}
                                    bundles={allBundles}
                                    homeSlideImages={importAll(homeSlideImagesGlob)}
                                    arrivalMode={arrivalMode}
                                    onArrivalChange={handleArrivalChange}
                                />
                            }
                        />
                <Route
                    path="/About"
                    element={
                        <Suspense fallback={<div className="homeBodyContainer">Loading...</div>}>
                            <About />
                        </Suspense>
                    }
                />
                <Route
                    path="/Contact"
                    element={
                        <Suspense fallback={<div className="homeBodyContainer">Loading...</div>}>
                            <Contact tours={tours} bundles={allBundles} arrivalMode={arrivalMode} />
                        </Suspense>
                    }
                />
                <Route
                    path="/Covid"
                    element={
                        <Suspense fallback={<div className="homeBodyContainer">Loading...</div>}>
                            <Covid />
                        </Suspense>
                    }
                />
                <Route
                    path="/BookNow"
                    element={
                        <Suspense fallback={<div className="homeBodyContainer">Loading booking...</div>}>
                            <BookNow tours={tours} bundles={allBundles} arrivalMode={arrivalMode} />
                        </Suspense>
                    }
                />
                    <Route
                        path="/FAQ"
                        element={
                            <Suspense fallback={<div className="homeBodyContainer">Loading...</div>}>
                                <FAQ />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/Safety"
                        element={
                            <Suspense fallback={<div className="homeBodyContainer">Loading...</div>}>
                                <Safety />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/ThankYou"
                        element={
                            <Suspense fallback={<div className="homeBodyContainer">Loading...</div>}>
                                <ThankYou />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/ThankYouContact"
                        element={
                            <Suspense fallback={<div className="homeBodyContainer">Loading...</div>}>
                                <ThankYouContact />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/tours/allTours"
                        element={
                            <Suspense fallback={<div className="homeBodyContainer">Loading tours...</div>}>
                                <AllTours tours={tours} />
                            </Suspense>
                        }
                    />
                    <Route
                        path="/tours/cruiseTours"
                        element={
                            <Suspense fallback={<div className="homeBodyContainer">Loading tours...</div>}>
                                <CruiseTours tours={tours} />
                            </Suspense>
                        }
                    />
                    {tours.map(tour => (
                        <Route
                            key={tour.name}
                            path={tour.url}
                            element={
                                <Suspense fallback={<div className="homeBodyContainer">Loading tour...</div>}>
                                    <TourBody tourInfo={tour} tours={tours} bundles={allBundles} arrivalMode={arrivalMode} />
                                </Suspense>
                            }
                        />
                    ))}
                    <Route
                        path="/admin/*"
                        element={
                            <Suspense fallback={<div className="homeBodyContainer">Loading admin...</div>}>
                                <AdminRoutes />
                            </Suspense>
                        }
                    />
            </Routes>

            <Footer followUs={followUs} bottomBarGridItems={siteUrls} />
        </Router>
    );
}

export default App;
